import { useState } from "react";
import { useAuthenticationContext } from "../../context/AuthenticationContext";
import { OnboardingQuestionType } from "../../models/types";
import { answerOnboardingQuestion } from "../../services/OnboardingService";
import { getFormCompleted } from "../../services/UserService";
import { Button } from "../Button";
import BaseModal from "./BaseModal";

// Define the questions and options for our onboarding
type Question = {
  id: OnboardingQuestionType;
  title: string;
  type: "single_select" | "multiple_select" | "text_input" | "date";
  options?: string[];
  placeholder?: string;
  dateFormat?: string; // Format for display, e.g., "DD/MM/YYYY"
}

const onboardingQuestions: Question[] = [
  {
    id: "user_birth",
    title: "Qual sua data de nascimento?",
    type: "date",
    placeholder: "DD/MM/AAAA",
    dateFormat: "DD/MM/YYYY"
  },
  {
    id: "financial_goal",
    title: "Qual é o seu principal objetivo financeiro?",
    type: "single_select",
    options: [
      "Organizar minhas finanças",
      "Aprender a investir",
      "Guardar dinheiro",
      "Sair das dívidas",
    ]
  },
  {
    id: "income_range",
    title: "Qual é a sua faixa de renda mensal?",
    type: "single_select",
    options: [
      "Até R$ 2.000",
      "R$ 2.001 a R$ 5.000",
      "R$ 5.001 a R$ 10.000",
      "Acima de R$ 10.000",
    ]
  },
  {
    id: "financial_control",
    title: "Como você organiza suas finanças atualmente?",
    type: "single_select",
    options: [
      "Não organizo",
      "Uso planilhas",
      "Uso aplicativos",
      "Faço anotações manuais",
      "Outro método"
    ]
  },
  {
    id: "finances_feeling",
    title: "Como você se sente com suas finanças hoje?",
    type: "single_select",
    options: [
      "Estressado(a)",
      "Incerto(a)",
      "Estável",
      "Confiante",
    ]
  },
  {
    id: "home_type",
    title: "Qual alternativa melhor descreve sua situação atual?",
    type: "single_select",
    options: [
      "Moro em casa própria",
      "Moro de aluguel",
      "Outras opções",
    ]
  },
  {
    id: "finance_debts",
    title: "Você tem alguma dívida hoje?",
    type: "multiple_select",
    options: [
      "Cartão de crédito",
      "Financiamento",
      "Carro",
      "Empréstimo",
      "Nenhuma dívida"
    ]
  },
];

interface OnboardingModalProps {
  open: boolean;
  onComplete: () => void;
}

export default function OnboardingModal({ open, onComplete }: OnboardingModalProps) {
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string | string[]>>({});
  const [textInputValue, setTextInputValue] = useState<string>("");
  const [selectedOptions, setSelectedOptions] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const { user, setUser } = useAuthenticationContext();

  const currentQuestion = onboardingQuestions[currentQuestionIndex];
  const progress = ((currentQuestionIndex + 1) / onboardingQuestions.length) * 100;

  // For date fields
  const [dateValue, setDateValue] = useState<string>("");

  // Reset input states when question changes
  const resetQuestionState = () => {
    setTextInputValue("");
    setSelectedOptions([]);
    setDateValue("");
  };

  // Check if current question has a valid answer
  const hasValidAnswer = () => {
    const answer = answers[currentQuestion.id];

    if (currentQuestion.type === "single_select") {
      return !!answer;
    } else if (currentQuestion.type === "multiple_select") {
      return Array.isArray(answer) && answer.length > 0;
    } else if (currentQuestion.type === "text_input" || currentQuestion.type === "date") {
      return !!answer && typeof answer === "string" && answer.trim() !== "";
    }

    return false;
  };

  // Move to the next question
  const goToNextQuestion = () => {
    if (currentQuestionIndex < onboardingQuestions.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
      resetQuestionState();
    } else {
      handleCompleteOnboarding();
    }
  };

  // Handle single select option click
  const handleSingleOptionSelect = (option: string) => {
    setAnswers(prev => ({
      ...prev,
      [currentQuestion.id]: option
    }));

    answerOnboardingQuestion({
      question: currentQuestion.id,
      answer: option,
    })

    // Auto-advance to next question
    goToNextQuestion();
  };

  // Handle multiple select option toggle
  const handleMultipleOptionToggle = (option: string) => {
    let newSelectedOptions: string[];

    // If the option is already selected, remove it
    if (selectedOptions.includes(option)) {
      newSelectedOptions = selectedOptions.filter(item => item !== option);
    } else {
      // Handle "Nenhuma dívida" specially - it should deselect all other options
      if (option === "Nenhuma dívida") {
        newSelectedOptions = ["Nenhuma dívida"];
      } else {
        // If selecting any other option, remove "Nenhuma dívida" if present
        const filteredOptions = selectedOptions.filter(item => item !== "Nenhuma dívida");
        newSelectedOptions = [...filteredOptions, option];
      }
    }

    setSelectedOptions(newSelectedOptions);

    // Update answers
    setAnswers(prev => ({
      ...prev,
      [currentQuestion.id]: newSelectedOptions
    }));
  };

  // Handle text input change
  const handleTextInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setTextInputValue(value);

    setAnswers(prev => ({
      ...prev,
      [currentQuestion.id]: value
    }));
  };

  // Handle date input change
  const handleDateInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setDateValue(value);

    setAnswers(prev => ({
      ...prev,
      [currentQuestion.id]: value
    }));
  };

  // Handle manual next button click (for text input, date input, and multiple select)
  const handleNextClick = () => {
    if (currentQuestion.type === "multiple_select" && selectedOptions.length === 0) {
      return; // Don't proceed if no options selected for multiple select
    }

    if (currentQuestion.type === "text_input" && (!textInputValue || textInputValue.trim() === "")) {
      return; // Don't proceed if text input is empty
    }

    if (currentQuestion.type === "date" && (!dateValue || dateValue.trim() === "")) {
      return; // Don't proceed if date input is empty
    }
    console.log("type:", typeof answers[currentQuestion.id] === "string")
    if (typeof answers[currentQuestion.id] === "string") {
      answerOnboardingQuestion({
        question: currentQuestion.id,
        answer: (answers[currentQuestion.id] as string),
      })
    } else {
      answerOnboardingQuestion({
        question: currentQuestion.id,
        answer: (answers[currentQuestion.id] as string[]).join(","),
      })
    }
    if (currentQuestionIndex === onboardingQuestions.length - 1) {
      handleCompleteOnboarding();
    } else {
      goToNextQuestion();
    }
  };

  const handleCompleteOnboarding = async () => {
    setLoading(true);
    try {
      getFormCompleted();
      onComplete();
    } catch (error) {
      console.error("Failed to save onboarding answers:", error);
    } finally {
      setLoading(false);
    }
  };

  // Initialize field states when revisiting questions
  useState(() => {
    if (currentQuestion.type === "multiple_select") {
      const savedAnswer = answers[currentQuestion.id];
      if (savedAnswer && Array.isArray(savedAnswer)) {
        setSelectedOptions(savedAnswer);
      }
    } else if (currentQuestion.type === "text_input") {
      const savedAnswer = answers[currentQuestion.id];
      if (savedAnswer && typeof savedAnswer === "string") {
        setTextInputValue(savedAnswer);
      }
    } else if (currentQuestion.type === "date") {
      const savedAnswer = answers[currentQuestion.id];
      if (savedAnswer && typeof savedAnswer === "string") {
        setDateValue(savedAnswer);
      }
    }
  });

  return (
    <BaseModal open={open} onClose={() => { }}>
      <div className="flex w-full flex-col gap-4 py-2">
        <h2 className="text-lg font-semibold text-gray-900">Quase lá!</h2>
        <p className="text-sm text-gray-600">
          Responda algumas perguntas rápidas para personalizarmos sua experiência.
        </p>

        {/* Progress bar */}
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div
            className="bg-blue-600 h-2 rounded-full transition-all duration-300 ease-in-out"
            style={{ width: `${progress}%` }}
          />
        </div>

        <div className="mt-2">
          <h3 className="text-md font-medium text-gray-900 mb-3">
            {currentQuestion.title}
          </h3>

          {/* Single Select */}
          {currentQuestion.type === "single_select" && (
            <div className="space-y-2">
              {currentQuestion.options?.map((option) => (
                <button
                  key={option}
                  onClick={() => handleSingleOptionSelect(option)}
                  className={`w-full py-3 px-4 text-left text-sm rounded-lg border transition-colors hover:bg-blue-50
                    ${answers[currentQuestion.id] === option
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-300'
                    }`}
                >
                  {option}
                </button>
              ))}
            </div>
          )}

          {/* Multiple Select */}
          {currentQuestion.type === "multiple_select" && (
            <div className="space-y-2">
              {currentQuestion.options?.map((option) => (
                <button
                  key={option}
                  onClick={() => handleMultipleOptionToggle(option)}
                  className={`w-full py-3 px-4 text-left text-sm rounded-lg border transition-colors hover:bg-blue-50 flex items-center
                    ${selectedOptions.includes(option)
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-300'
                    }`}
                >
                  <div className={`w-5 h-5 mr-3 border rounded flex items-center justify-center
                    ${selectedOptions.includes(option)
                      ? 'bg-blue-500 border-blue-500'
                      : 'border-gray-300'
                    }`}
                  >
                    {selectedOptions.includes(option) && (
                      <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                      </svg>
                    )}
                  </div>
                  {option}
                </button>
              ))}
            </div>
          )}

          {/* Text Input */}
          {currentQuestion.type === "text_input" && (
            <div className="space-y-2">
              <input
                type="text"
                value={textInputValue}
                onChange={handleTextInputChange}
                placeholder={currentQuestion.placeholder || "Digite sua resposta"}
                className="w-full py-3 px-4 text-sm rounded-lg border border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
              />
            </div>
          )}

          {/* Date Input */}
          {currentQuestion.type === "date" && (
            <div className="space-y-2">
              <div className="relative">
                <input
                  type="date"
                  value={dateValue}
                  onChange={handleDateInputChange}
                  className="w-full py-3 px-4 text-sm rounded-lg border border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                />
              </div>
              <p className="text-xs text-gray-500">
                {currentQuestion.id === "user_birth" ? "Sua data de nascimento nos ajuda a personalizar sugestões financeiras" :
                  currentQuestion.id === "retirement_date" ? "Essa informação nos ajuda a planejar seus objetivos a longo prazo" : ""}
              </p>
            </div>
          )}
        </div>

        <div className="flex justify-between mt-4">
          {/* Back button */}
          {currentQuestionIndex > 0 && (
            <Button
              onClick={() => {
                setCurrentQuestionIndex(prev => prev - 1);
                resetQuestionState();
              }}
              title="Voltar"
              className="bg-white border border-gray-300 text-gray-700 hover:bg-gray-50"
            />
          )}

          {/* Next button for multiple select, text input, and date input */}
          {(currentQuestion.type === "multiple_select" ||
            currentQuestion.type === "text_input" ||
            currentQuestion.type === "date") &&
            currentQuestionIndex < onboardingQuestions.length - 1 && (
              <Button
                onClick={handleNextClick}
                title="Próximo"
                disabled={!hasValidAnswer()}
                className="ml-auto bg-blue-600 hover:bg-blue-700 text-white disabled:bg-blue-300 disabled:cursor-not-allowed"
              />
            )}

          {/* Complete button on the last question */}
          {currentQuestionIndex === onboardingQuestions.length - 1 && (
            <Button
              onClick={handleNextClick}
              title="Concluir"
              loading={loading}
              disabled={loading || !hasValidAnswer()}
              className="ml-auto bg-blue-600 hover:bg-blue-700 text-white disabled:bg-blue-300 disabled:cursor-not-allowed"
            />
          )}
        </div>
      </div>
    </BaseModal>
  );
}